import { retry } from './retry.ts'
import {
  isRetryableError,
  getRetryAfterMs,
  parseRetryAfterHeader,
  RETRYABLE_STATUS_CODES,
} from './retry-errors.ts'

export interface OnlineDetector {
  online: boolean
  waitForOnline(): Promise<void>
}

export interface RetryFetchOptions {
  /** Max retries (not counting initial). Default: Infinity */
  retries?: number
  /** Backoff factor. Default: 2 */
  factor?: number
  /** Initial delay ms. Default: 1000 (0 in test env) */
  minTimeout?: number
  /** Max delay cap ms. Default: 30_000 */
  maxTimeout?: number
  /** Jitter via (Math.random()+1) multiplier. Default: true */
  randomize?: boolean
  /** Online detector — waits for online before each attempt */
  onlineDetector?: OnlineDetector
  /** Override retry predicate. Default: isRetryableError(err) || RETRYABLE_STATUS_CODES.has(res.status) */
  retryOn?: (ctx: {
    attempt: number
    error: Error | null
    response: Response | null
  }) => boolean | Promise<boolean>
  /** Override delay. Receives computed backoff (already includes Retry-After floor). */
  retryDelay?: (ctx: {
    attempt: number
    error: Error | null
    response: Response | null
    computedDelayMs: number
  }) => number
}

export type FetchFn = (input: string, init?: RequestInit) => Promise<Response>

class RetryableResponseError extends Error {
  constructor(public readonly response: Response) {
    super(`Request failed with status ${response.status}`)
    this.name = 'RetryableResponseError'
  }
}

function extractResponseAndError(error: Error): {
  response: Response | null
  error: Error | null
} {
  if (error instanceof RetryableResponseError) {
    return { response: error.response, error: null }
  }
  return { response: null, error }
}

/**
 * Wraps a fetch function with retry logic using exponential backoff.
 * Retries on network errors and retryable HTTP status codes (408, 429, 502, 503, 504).
 * Respects Retry-After headers as a delay floor.
 */
export function createRetryFetch(
  baseFetch: FetchFn = fetch,
  options: RetryFetchOptions = {},
): FetchFn {
  const {
    retries = Number.POSITIVE_INFINITY,
    factor = 2,
    minTimeout,
    maxTimeout = 30_000,
    randomize = true,
    onlineDetector,
    retryOn,
    retryDelay,
  } = options

  return async (input, init) => {
    let lastResponse: Response | undefined

    try {
      return await retry(
        async () => {
          if (onlineDetector && !onlineDetector.online) {
            await onlineDetector.waitForOnline()
          }

          const response = await baseFetch(input, init)

          // Always wrap response so shouldRetry can inspect it.
          // shouldRetry decides whether to actually retry.
          throw new RetryableResponseError(response)
        },
        {
          retries,
          factor,
          ...(minTimeout !== undefined && { minTimeout }),
          maxTimeout,
          randomize,
          signal: init?.signal ?? undefined,

          shouldRetry: (ctx) => {
            const { response, error } = extractResponseAndError(ctx.error)
            if (response) lastResponse = response

            if (retryOn) {
              return retryOn({ attempt: ctx.attemptNumber, error, response })
            }

            if (response) return RETRYABLE_STATUS_CODES.has(response.status)
            return isRetryableError(ctx.error)
          },

          computeDelay: (ctx) => {
            const { response, error } = extractResponseAndError(ctx.error)

            // Retry-After from response header or error property
            let retryAfterMs: number | undefined
            if (response) {
              retryAfterMs = parseRetryAfterHeader(
                response.headers.get('Retry-After'),
              )
            }
            retryAfterMs ??= getRetryAfterMs(ctx.error)

            // Use Retry-After as floor for computed backoff
            const delayMs = Math.max(ctx.computedDelayMs, retryAfterMs ?? 0)

            if (retryDelay) {
              return retryDelay({
                attempt: ctx.attemptNumber,
                error,
                response,
                computedDelayMs: delayMs,
              })
            }

            return delayMs
          },
        },
      )
    } catch (error) {
      init?.signal?.throwIfAborted()
      // If retries exhausted on retryable status, return the response (standard fetch semantics)
      if (error instanceof RetryableResponseError) return error.response
      if (lastResponse) return lastResponse
      throw error
    }
  }
}
